import { useFormik } from 'formik'
import { FC, useMemo } from 'react'
import {
  DocumentoType,
  OptionsHelper,
  ButtonType,
  showError,
  showAlert,
  MaskType,
} from '../../../../../../../_metronic/helpers'
import { ClienteDtoCreate, ClienteDtoUpdate, ClienteView } from '../../core/_models'
import { createEmpresaCliente, updateEmpresaCliente } from '../../core/_request'
import {
  Button,
  FetchState,
  FormInput,
  FormRadio,
  FormSelect,
  LoadingIndicator,
  useModal,
} from '../../../../../../components'
import { empresaClienteSchema } from './validation/empresaClienteSchema'
import { useT } from '_metronic/i18n/Metronici18n'

export const EmpresasClienteModal: FC = () => {
  const { fetchStates, closeModal } = useModal()
  const { t } = useT()

  const { data: cliente } = (fetchStates['cliente'] || {}) as FetchState<ClienteView>

  const initialValues = {
    nome: cliente?.nome || '',
    cnpj: cliente?.tipoDocumento === DocumentoType.Cnpj ? cliente?.documento : '',
    cpf: cliente?.tipoDocumento === DocumentoType.Cpf ? cliente?.documento : '',
    caepf: cliente?.tipoDocumento === DocumentoType.Caepf ? cliente?.documento : '',
    tipo: cliente?.tipoDocumento,
    empresa: cliente ? { label: cliente.empresa?.nomeFantasia, value: cliente.empresa?.id } : null,
  }

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema: useMemo(() => empresaClienteSchema(t), [t]),
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true)
      try {
        if (cliente) {
          const client: ClienteDtoUpdate = {
            tipo: values.tipo as DocumentoType,
            ativo: cliente.ativo,
            nome: values.nome,
            caepf: values.tipo === DocumentoType.Caepf ? values.caepf : undefined,
            cpf: values.tipo === DocumentoType.Cpf ? values.cpf : undefined,
            cnpj: values.tipo === DocumentoType.Cnpj ? values.cnpj : undefined,
          }
          await updateEmpresaCliente(cliente.id, client)
          showAlert({
            title: 'Sucesso!',
            text: 'Empregador ASO alterada com sucesso!',
            icon: 'success',
          })
        } else {
          const client: ClienteDtoCreate = {
            nome: values.nome,
            tipo: values.tipo as DocumentoType,
            caepf: values.tipo === DocumentoType.Caepf ? values.caepf : undefined,
            cpf: values.tipo === DocumentoType.Cpf ? values.cpf : undefined,
            cnpj: values.tipo === DocumentoType.Cnpj ? values.cnpj : undefined,
          }
          await createEmpresaCliente(values.empresa?.value, client)
          showAlert({
            title: 'Sucesso!',
            text: 'Empregador ASO criada com sucesso!',
            icon: 'success',
          })
        }
        closeModal()
      } catch (error: any) {
        showError(error)
      } finally {
        setSubmitting(false)
      }
    },
  })

  return (
    <form onSubmit={formik.handleSubmit} noValidate>
      <FormSelect
        formik={formik}
        entity='empresa'
        fetchOptions={OptionsHelper.getOptionsEmpresas}
        name='empresa'
        className='mb-3'
        isDisabled={!!cliente}
        isRequired
      />
      <FormInput
        formik={formik}
        label='Nome'
        name='nome'
        placeholder='Digite o nome do empregador'
        className='mb-4'
        isRequired
      />
      <FormRadio
        formik={formik}
        name='tipo'
        label='Tipo:'
        options={[
          { id: 1, descricao: DocumentoType.Cpf, value: DocumentoType.Cpf },
          { id: 2, descricao: DocumentoType.Cnpj, value: DocumentoType.Cnpj },
          { id: 3, descricao: DocumentoType.Caepf, value: DocumentoType.Caepf },
        ]}
        className='mb-3'
        isRequired
        isInline
      />

      {formik.values.tipo === DocumentoType.Cpf && (
        <FormInput
          formik={formik}
          label='CPF'
          name='cpf'
          placeholder='Digite o CPF'
          mask={MaskType.CPF}
          isRequired
        />
      )}
      {formik.values.tipo === DocumentoType.Cnpj && (
        <FormInput
          formik={formik}
          label='CNPJ'
          name='cnpj'
          placeholder='Digite o CNPJ'
          mask={MaskType.CNPJ}
          isRequired
        />
      )}
      {formik.values.tipo === DocumentoType.Caepf && (
        <FormInput
          formik={formik}
          label='CAEPF'
          name='caepf'
          placeholder='Digite o CAEPF'
          mask={MaskType.CAEPF}
          isRequired
        />
      )}

      <div className='d-flex justify-content-center mt-10'>
        <Button type={ButtonType.Reset} onClick={closeModal} className='btn-secondary me-3'>
          {t('common.cancel')}
        </Button>
        <Button type={ButtonType.Submit} className='btn-primary'>
          {formik.isSubmitting ? <LoadingIndicator /> : t('common.send')}
        </Button>
      </div>
    </form>
  )
}
